import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

/**
 * Configuration UI par secteur d'activité (industry_type).
 *
 * Source de vérité :
 *   - Le backend expose /api/industry/types avec le catalog officiel
 *   - L'industry_type effectif d'un user vient de sa licence/client
 *
 * Ce hook fournit aux composants frontend :
 *   - Le `industry` courant (manufacturing | datacenter | office)
 *   - Les `labels` adaptés (zoneLabel, lineLabel, mainKpiLabel...)
 *   - La liste des `dashboards` visibles
 *   - Les `loadCategories` disponibles (utiles dans les forms)
 *
 * Usage :
 *   const { industry, labels, dashboards } = useIndustryConfig();
 *   <h2>{labels.zoneLabel}</h2>  // "Atelier" | "Salle" | "Étage"
 */

// Configuration UI hardcodée côté front (synchronisée avec INDUSTRY_TYPES backend).
// Source de vérité métier reste le backend ; ces labels sont le rendu utilisateur.
const INDUSTRY_PROFILES = {
  manufacturing: {
    label: 'Industrie / Production',
    zoneLabel: 'Atelier',
    zoneLabelPlural: 'Ateliers',
    lineLabel: 'Ligne',
    lineLabelPlural: 'Lignes',
    breakerLabel: 'Disjoncteur',
    mainKpiLabel: 'kWh/tonne',
    mainKpiHint: 'Intensité énergétique de production',
    showSurface: false,
    showProduction: true,
    showLoadCategory: false,            // total partout, dimension non utilisée
    operationalRole: 'production',
    facilityRole: 'maintenance',
    roleLabels: {
      production: 'Production',
      chef_production: 'Chef de Production',
      maintenance: 'Maintenance',
      direction: 'Direction',
      direction_centrale: 'Direction Centrale',
      admin_client: 'Administrateur Client',
      analyste: 'Analyste',
      energy_manager: 'Energy Manager (énergie & coûts)',
      affichage: 'Affichage (écran usine)',
    },
    dashboards: [
      'production', 'chef-production', 'maintenance', 'direction',
      'zones', 'comparison', 'breakers',
      'alerts', 'multi-site', 'production-metrics',
    ],
  },
  datacenter: {
    label: 'Datacenter / Salle serveurs',
    zoneLabel: 'Salle',
    zoneLabelPlural: 'Salles',
    lineLabel: 'Disjoncteur',
    lineLabelPlural: 'Disjoncteurs',
    breakerLabel: 'Disjoncteur',
    mainKpiLabel: 'PUE',
    mainKpiHint: 'Power Usage Effectiveness (Total / IT)',
    showSurface: true,
    showProduction: false,
    showLoadCategory: true,             // crucial pour calculer le PUE
    operationalRole: 'dc_exploitation',
    facilityRole: 'dc_facility',
    roleLabels: {
      dc_exploitation: 'Exploitation (NOC / DCIM)',
      dc_facility: 'Facility / Génie clim & élec',
      direction: 'Direction',
      direction_centrale: 'Direction Centrale',
      admin_client: 'Administrateur Client',
      analyste: 'Analyste',
      energy_manager: 'Energy Manager (énergie & coûts)',
      affichage: 'Affichage (écran usine)',
    },
    dashboards: [
      'dc-exploitation',                 // ops 24/7 (PUE temps réel, IT load, capacity)
      'dc-facility',                     // génie clim/élec (cooling, redondance, qualité)
      'dc-redundancy',                   // redondance 2N (voies A/B en miroir)
      'facility',                        // dashboard PUE détaillé (transverse datacenter)
      'direction',                       // dashboard transverse business (coûts, CO2)
      'multi-site', 'comparison', 'breakers',
      'alerts',
    ],
  },
  office: {
    label: 'Bureaux / Tertiaire',
    zoneLabel: 'Étage',
    zoneLabelPlural: 'Étages',
    lineLabel: 'Zone',
    lineLabelPlural: 'Zones',
    breakerLabel: 'Disjoncteur',
    mainKpiLabel: 'kWh/m²',
    mainKpiHint: 'Consommation surfacique',
    showSurface: true,
    showProduction: false,
    showLoadCategory: false,
    operationalRole: 'building_manager',
    facilityRole: 'building_technician',
    roleLabels: {
      building_manager: 'Gestionnaire bâtiment (FM)',
      building_technician: 'Technicien bâtiment',
      direction: 'Direction',
      direction_centrale: 'Direction Centrale',
      admin_client: 'Administrateur Client',
      analyste: 'Analyste',
      energy_manager: 'Energy Manager (énergie & coûts)',
      affichage: 'Affichage (écran usine)',
    },
    dashboards: [
      'building-manager',                // gestion quotidienne (kWh/m², coûts, occupation)
      'building-technician',             // technique (circuits, qualité réseau, harmoniques)
      'energy-density',                  // dashboard kWh/m² détaillé (transverse office)
      'direction',                       // dashboard transverse business (coûts, CO2)
      'multi-site', 'comparison', 'breakers',
      'alerts',
    ],
  },
  // ────────────────────────────────────────────────────────────────────
  // water_production : production et distribution d eau potable (SODECI).
  // Phase 1 : monitoring electrique des pompes/moteurs (kWh, heures marche,
  // demarrages, equilibre courant, cos phi). Phase 2 ulterieure :
  // capteurs debit/pression -> kWh/m3 et rendement hydraulique.
  // Echelle : 1 a 50 pompes par station.
  // ────────────────────────────────────────────────────────────────────
  water_production: {
    label: "Production d'eau potable / Stations de pompage",
    zoneLabel: 'Station',
    zoneLabelPlural: 'Stations',
    lineLabel: 'Pompe',
    lineLabelPlural: 'Pompes',
    breakerLabel: 'Disjoncteur',
    mainKpiLabel: 'kWh/m³',
    mainKpiHint: "Énergie spécifique de pompage (Phase 2 : capteurs débit requis). Phase 1 affiche kWh/h de fonctionnement comme proxy.",
    showSurface: false,
    showProduction: true,                // volume d'eau pompé = production (Phase 2)
    showLoadCategory: false,
    operationalRole: 'production',       // réutilise role existant -> libellé "Opérateur pompage"
    facilityRole: 'maintenance',         // réutilise role existant -> libellé "Maintenance pompage"
    roleLabels: {
      production: 'Opérateur pompage',
      chef_production: 'Chef de station',
      maintenance: 'Maintenance pompage',
      direction: 'Direction',
      direction_centrale: 'Direction Centrale (multi-stations)',
      admin_client: 'Administrateur Client',
      analyste: 'Analyste',
      energy_manager: 'Energy Manager (énergie & coûts)',
      affichage: 'Affichage (écran usine)',
    },
    dashboards: [
      'pumping-station',                 // vue par station : liste pompes + KPIs operationnels
      'maintenance',                     // qualite reseau + alertes mecaniques (reutilise)
      'direction',                       // KPIs financiers (cout elec, kWh) (reutilise)
      'zones',                           // vue agregee par station = zone (reutilise)
      'multi-site',                      // carte multi-stations (reutilise)
      'comparison', 'breakers', 'alerts',
    ],
  },
};

// Libellés par défaut quand l'industry du user ne couvre pas un rôle (ex. super_admin)
const ROLE_FALLBACK_LABELS = {
  super_admin: 'Super Administrateur',
  admin: 'Administrateur',
  admin_client: 'Administrateur Client',
  direction: 'Direction',
  direction_centrale: 'Direction Centrale',
  analyste: 'Analyste',
  production: 'Production',
  maintenance: 'Maintenance',
  dc_exploitation: 'Exploitation (NOC / DCIM)',
  dc_facility: 'Facility / Génie clim & élec',
  building_manager: 'Gestionnaire bâtiment (FM)',
  building_technician: 'Technicien bâtiment',
  energy_manager: 'Energy Manager (énergie & coûts)',
  affichage: 'Affichage (écran usine)',
};

const LOAD_CATEGORIES = [
  { value: 'total', label: 'Total (par défaut)', hint: 'Disjoncteur de comptage global' },
  { value: 'it', label: 'Charge IT', hint: 'Baies serveurs / équipements informatiques (datacenter)' },
  { value: 'cooling', label: 'Climatisation', hint: 'CRAC, CRAH, climatiseurs' },
  { value: 'lighting', label: 'Éclairage', hint: 'Éclairage général ou de sécurité' },
  { value: 'other', label: 'Autre', hint: 'Charges diverses non catégorisées' },
];

const INDUSTRY_OPTIONS = Object.entries(INDUSTRY_PROFILES).map(([value, p]) => ({
  value,
  label: p.label,
  mainKpi: p.mainKpiLabel,
}));

const DEFAULT_INDUSTRY = 'manufacturing';

export function useIndustryConfig() {
  const { user } = useAuth();
  const [industry, setIndustry] = useState(DEFAULT_INDUSTRY);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) {
      setIndustry(DEFAULT_INDUSTRY);
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Le backend renvoie l'industry du user via sa licence.
        // En l'absence d'endpoint dédié, on déduit via le 1er site de l'user
        // (simplification ; on pourra ajouter /api/auth/me étendu plus tard).
        const sites = user.site_ids || (user.site_id ? [user.site_id] : []);
        if (sites.length === 0) {
          if (!cancelled) {
            setIndustry(DEFAULT_INDUSTRY);
            setLoaded(true);
          }
          return;
        }
        const data = await api.request(
          `/api/industry/sites/${encodeURIComponent(sites[0])}/kpis?period_hours=1`,
          { method: 'GET' },
        );
        if (!cancelled && data && data.industry_type && INDUSTRY_PROFILES[data.industry_type]) {
          setIndustry(data.industry_type);
        }
      } catch (err) {
        // Échec silencieux → on reste sur manufacturing par défaut
        // (Console-warn en dev pour debug)
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[useIndustryConfig] fallback to default industry:', err?.message);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const profile = INDUSTRY_PROFILES[industry] || INDUSTRY_PROFILES[DEFAULT_INDUSTRY];

  return useMemo(() => ({
    industry,
    loaded,
    labels: {
      industryLabel: profile.label,
      zoneLabel: profile.zoneLabel,
      zoneLabelPlural: profile.zoneLabelPlural,
      lineLabel: profile.lineLabel,
      lineLabelPlural: profile.lineLabelPlural,
      breakerLabel: profile.breakerLabel,
      mainKpiLabel: profile.mainKpiLabel,
      mainKpiHint: profile.mainKpiHint,
    },
    flags: {
      showSurface: profile.showSurface,
      showProduction: profile.showProduction,
      showLoadCategory: profile.showLoadCategory,
    },
    dashboards: profile.dashboards,
    isDashboardVisible: (key) => profile.dashboards.includes(key),
    operationalRole: profile.operationalRole,    // 'production' | 'dc_exploitation' | 'building_manager'
    facilityRole: profile.facilityRole,          // 'maintenance' | 'dc_facility' | 'building_technician'
    roleLabels: profile.roleLabels || {},
    getRoleLabel: (role) => (profile.roleLabels && profile.roleLabels[role]) || ROLE_FALLBACK_LABELS[role] || role,
    setIndustry,                  // permet le test/debug en dev
  }), [industry, loaded, profile]);
}

// Exports utilitaires (utilisés par les forms)
export { INDUSTRY_OPTIONS, LOAD_CATEGORIES, INDUSTRY_PROFILES, ROLE_FALLBACK_LABELS };

// Liste des rôles sélectionnables côté form, filtrée par industry.
// Manufacturing → production/maintenance ; datacenter → dc_* ; office → building_*.
// Les rôles transverses sont toujours présents.
export function getSelectableRolesForIndustry(industry) {
  const transverse = [
    { value: 'admin_client',       label: 'Administrateur Client' },
    { value: 'direction_centrale', label: 'Direction Centrale (multi-sites)' },
    { value: 'direction',          label: 'Direction (1 site)' },
    { value: 'analyste',           label: 'Analyste (lecture seule)' },
    { value: 'energy_manager',     label: 'Energy Manager (suivi énergie & coûts)' },
    { value: 'affichage',          label: 'Affichage (écran usine : lecture seule)' },
  ];
  if (industry === 'datacenter') {
    return [
      { value: 'dc_exploitation', label: 'Exploitation (NOC / DCIM)' },
      { value: 'dc_facility',     label: 'Facility / Génie clim & élec' },
      ...transverse,
    ];
  }
  if (industry === 'office') {
    return [
      { value: 'building_manager',    label: 'Gestionnaire bâtiment (FM)' },
      { value: 'building_technician', label: 'Technicien bâtiment' },
      ...transverse,
    ];
  }
  if (industry === 'water_production') {
    return [
      { value: 'production',       label: 'Opérateur pompage' },
      { value: 'chef_production',  label: 'Chef de station' },
      { value: 'maintenance',      label: 'Maintenance pompage' },
      ...transverse,
    ];
  }
  // manufacturing (défaut)
  return [
    { value: 'production',       label: 'Production' },
    { value: 'chef_production',  label: 'Chef de Production (autorise + valide saisies tonnage)' },
    { value: 'maintenance',      label: 'Maintenance' },
    ...transverse,
  ];
}
